import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { Upload, Loader2, Info } from 'lucide-react';
import { ACCEPT_ATTR, MAX_FILE_SIZE_MB, type StagedSource } from '../types';
import { SupportedFormatsModal } from '../SupportedFormatsModal';
import { useStageFiles } from '../useStageFiles';
import { useParseLimits } from '../useParseLimits';

interface UploadFileTabProps {
  onAdd: (source: StagedSource) => void | Promise<void>;
  remainingFileSlots?: number;
}

export interface TabFocusHandle {
  focus: () => void;
}

export const UploadFileTab = forwardRef<TabFocusHandle, UploadFileTabProps>(
  function UploadFileTab({ onAdd, remainingFileSlots }, ref) {
    const inputRef = useRef<HTMLInputElement>(null);
    const dropZoneRef = useRef<HTMLDivElement>(null);
    const [isDragOver, setIsDragOver] = useState(false);
    const [showFormats, setShowFormats] = useState(false);

    useImperativeHandle(ref, () => ({
      // Focus the drop zone button (visible) instead of the hidden input
      // so the focus ring is actually perceivable.
      focus: () => dropZoneRef.current?.focus(),
    }));

    const { stageFiles, isBusy } = useStageFiles(onAdd, remainingFileSlots);
    const limits = useParseLimits();

    const handleDrop = (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      const dropped = Array.from(e.dataTransfer.files);
      if (dropped.length > 0) void stageFiles(dropped);
    };

    const handlePicked = (e: React.ChangeEvent<HTMLInputElement>) => {
      const picked = Array.from(e.target.files ?? []);
      // reset so the same file can be re-selected after removal
      e.target.value = '';
      if (picked.length > 0) void stageFiles(picked);
    };

    const noSlots = remainingFileSlots === 0;

    return (
      <div className='flex h-full flex-col gap-3 px-6 pb-6 pt-6'>
        <div className='flex justify-end'>
          <button
            type='button'
            onClick={() => setShowFormats(true)}
            className='tap-target flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-brand-50 hover:text-brand-700 dark:hover:bg-primary/30 dark:hover:text-primary'
          >
            <Info className='h-3.5 w-3.5' />
            Supported file types
          </button>
        </div>

        <div
          ref={dropZoneRef}
          role='button'
          tabIndex={noSlots || isBusy ? -1 : 0}
          aria-label='Drop files here or click to browse'
          aria-disabled={noSlots}
          onClick={() => {
            if (!noSlots && !isBusy) inputRef.current?.click();
          }}
          onKeyDown={e => {
            if ((e.key === 'Enter' || e.key === ' ') && !noSlots && !isBusy) {
              e.preventDefault();
              inputRef.current?.click();
            }
          }}
          onDragOver={e => {
            e.preventDefault();
            if (!noSlots) setIsDragOver(true);
          }}
          onDragLeave={e => {
            e.preventDefault();
            setIsDragOver(false);
          }}
          onDrop={noSlots ? undefined : handleDrop}
          className={`group relative flex min-h-[280px] flex-1 cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed text-center transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400/60 dark:focus-visible:ring-primary/60 ${
            isDragOver
              ? 'scale-[1.01] border-brand-400 bg-brand-50/70 dark:border-primary dark:bg-tint-brand'
              : 'border-gray-300/70 bg-white/30 hover:border-brand-300/80 hover:bg-brand-50/30 dark:border-white/20 dark:bg-white/[0.02] dark:hover:border-primary/60 dark:hover:bg-white/[0.04]'
          } ${noSlots || isBusy ? 'cursor-not-allowed opacity-50' : ''}`}
        >
          {isBusy ? (
            <div className='flex flex-col items-center gap-2'>
              <Loader2 className='h-8 w-8 animate-spin text-brand-500 dark:text-primary' />
              <p className='text-sm text-muted-foreground'>Adding…</p>
            </div>
          ) : (
            <>
              <div
                className={`mb-3 flex h-14 w-14 items-center justify-center rounded-full transition-colors ${
                  isDragOver
                    ? 'dark:tint-brand-strong bg-brand-100 text-brand-600'
                    : 'bg-brand-50 text-brand-500 group-hover:bg-brand-100 group-hover:text-brand-600 dark:bg-muted dark:text-primary dark:group-hover:bg-primary/50 dark:group-hover:text-primary'
                }`}
              >
                <Upload className='h-5 w-5' />
              </div>
              <p className='mb-1.5 text-sm font-medium text-foreground'>
                Drop files here, or click anywhere to browse
              </p>
              <p className='text-xs text-muted-foreground'>
                Up to {MAX_FILE_SIZE_MB} MB per file
                {remainingFileSlots !== undefined &&
                  ` · ${remainingFileSlots} slot${remainingFileSlots === 1 ? '' : 's'} left`}
                {limits.note}
              </p>
            </>
          )}

          {/* Hidden file input - triggered programmatically via the drop-zone
              button. Kept out of layout entirely (`hidden`) so the OS doesn't
              render its native "No file chosen" tooltip. */}
          <input
            ref={inputRef}
            type='file'
            multiple
            accept={ACCEPT_ATTR}
            onChange={handlePicked}
            disabled={noSlots}
            className='hidden'
            tabIndex={-1}
            aria-hidden='true'
          />
        </div>

        <SupportedFormatsModal
          isOpen={showFormats}
          onClose={() => setShowFormats(false)}
        />
      </div>
    );
  }
);
